import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import LandingPage from "./pages/LandingPage";
import PublicAppDirectoryPage from "./pages/PublicAppDirectoryPage";
import LoginPage from "./pages/LoginPage";
import AppLayout from "./pages/AppLayout";
import DashboardPage from "./pages/DashboardPage";
import WorkspacesPage from "./pages/WorkspacesPage";
import BrandDNAPage from "./pages/BrandDNAPage";
import AdsPage from "./pages/AdsPage";
import LibraryPage from "./pages/LibraryPage";
import LibraryItemPage from "./pages/LibraryItemPage";
import UpgradePage from "./pages/UpgradePage";
import StudioPage from "./pages/StudioPage";
import PackBuilderPage from "./pages/PackBuilderPage";
import MarketplacePage from "./pages/MarketplacePage";
import MembersPage from "./pages/MembersPage";
import BrandingPage from "./pages/BrandingPage";
import WebsiteBuilderPage from "./pages/WebsiteBuilderPage";
import CampaignsPage from "./pages/CampaignsPage";
import CampaignDetailPage from "./pages/CampaignDetailPage";
import ConnectionsPage from "./pages/ConnectionsPage";
import ConnectionsCallbackPage from "./pages/ConnectionsCallbackPage";
import DropshippingPage from "./pages/DropshippingPage";
import AvatarLibraryPage from "./pages/AvatarLibraryPage";
import AvatarStudioPage from "./pages/AvatarStudioPage";
import AdminPage from "./pages/AdminPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import RefundPage from "./pages/RefundPage";
import ContactPage from "./pages/ContactPage";
import ClipStudioPage from "./pages/ClipStudioPage";
import AdvancedBuilderPage from "./pages/AdvancedBuilderPage";
import ContentCalendarPage from "./pages/ContentCalendarPage";
import PublicWebPage from "./pages/PublicWebPage";
import PageNotFound from "./pages/PageNotFound";
import { session } from "./lib/session";
import { auth } from "./lib/auth";
import AuthCallbackPage from "./pages/AuthCallbackPage";

function ProtectedRoute({ children }) {
  const location = useLocation();
  // A password grant completed in LoginPage just before this route mounted.
  // Do not race that handoff with another restore request. A refresh or future
  // visit still takes the normal validated restore path below.
  const [justSignedIn] = useState(() => Boolean(location.state?.justSignedIn && auth.getEmail()));
  const [email, setEmail] = useState(() => justSignedIn ? auth.getEmail() : null);
  const [loading, setLoading] = useState(() => !justSignedIn);

  useEffect(() => {
    if (justSignedIn) return;
    auth.restore().then((active) => {
      setEmail(active?.email || null);
      setLoading(false);
    });
  }, [justSignedIn]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!email) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function LandingRoute() {
  const navigate = useNavigate();

  useEffect(() => {
    // Supabase recovery links can fall back to Site URL when a mail template
    // omits redirect_to. Preserve the fragment and hand it to the only route
    // that is allowed to process a recovery session.
    if (window.location.hash.includes("access_token=")) {
      navigate(`/auth/callback${window.location.search}${window.location.hash}`, { replace: true });
    }
  }, [navigate]);

  return <LandingPage />;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingRoute />} />
        <Route path="/apps" element={<PublicAppDirectoryPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/refund" element={<RefundPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/w/:slug" element={<PublicWebPage />} />
        <Route path="/avatar-studio" element={<Navigate to="/app/studio/avatar" replace />} />
        <Route
          path="/app/*"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Routes>
                  <Route index element={<DashboardPage />} />
                  <Route path="workspaces" element={<WorkspacesPage />} />
                  <Route path="brand" element={<BrandDNAPage />} />
                  <Route path="brand-dna" element={<BrandDNAPage />} />
                  <Route path="branding" element={<BrandingPage />} />
                  <Route path="website" element={<WebsiteBuilderPage />} />
                  <Route path="campaigns" element={<CampaignsPage />} />
                  <Route path="campaigns/:campaignId" element={<CampaignDetailPage />} />
                  <Route path="connections" element={<ConnectionsPage />} />
                  <Route path="connections/callback" element={<ConnectionsCallbackPage />} />
                  <Route path="dropshipping" element={<DropshippingPage />} />
                  <Route path="ads" element={<AdsPage />} />
                  <Route path="avatars" element={<AvatarLibraryPage />} />
                  <Route path="studio" element={<StudioPage />} />
                  <Route path="studio/packs" element={<PackBuilderPage />} />
                  <Route path="studio/avatar" element={<AvatarStudioPage />} />
                  <Route path="clip-studio" element={<ClipStudioPage />} />
                  <Route path="builder" element={<AdvancedBuilderPage />} />
                  <Route path="content-calendar" element={<ContentCalendarPage />} />
                  <Route path="studio/avatars" element={<AvatarStudioPage />} />
                  <Route path="marketplace" element={<MarketplacePage />} />
                  <Route path="members" element={<MembersPage />} />
                  <Route path="library" element={<LibraryPage />} />
                  <Route path="library/:id" element={<LibraryItemPage />} />
                  <Route path="upgrade" element={<UpgradePage />} />
                  <Route path="admin" element={<AdminPage />} />
                  <Route path="*" element={<PageNotFound />} />
                </Routes>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
